import { ChangeEvent, useEffect, useRef } from 'react';

import { dateStore } from '../store';
import { useTranslation } from 'react-i18next';
import { CustomCalendarProps } from '../types';
import dayjs from 'dayjs';
import { CustomDatepickerLocaleKey } from 'src/modules/locales/types';
import { customDatepickerLocales } from 'src/modules/locales/config';

/**
 * Кастомный хук для управления поведением календаря, включая локализацию, изменение значений и управление состоянием.
 * @param restProps - Свойства, передаваемые календарному компоненту.
 * @returns Объект, содержащий ссылку на элемент input.
 */
export const useCustomCalendar = (restProps: CustomCalendarProps) => {
  const {
    i18n: { language }
  } = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const initialRender = useRef<boolean>(true);
  const currentLanguage = useRef<string | null>(null);
  const { value, defaultValue } = restProps || {};

  /**
   * Эффект для обработки локализации календаря на основе текущего языка.
   * - Динамически загружает данные локали для `dayjs` при смене языка.
   */
  useEffect(() => {
    if (!customDatepickerLocales[language as CustomDatepickerLocaleKey]) {
      return;
    }
    customDatepickerLocales[language as CustomDatepickerLocaleKey]().then((lang) => {
      dayjs.locale(lang.name);
      if (!value && !defaultValue) {
        dateStore.resetDateStore();
        currentLanguage.current = language;
      }
    });
  }, [language]);

  /**
   * Эффект для отслеживания изменений атрибута `value` у элемента input и вызова колбэка `onChange`.
   * - Использует `MutationObserver` для отслеживания изменений атрибута `value`.
   * - Вызывает `onChange` из `restProps` при изменении значения.
   */
  useEffect(() => {
    const input = inputRef.current;

    if (!input) return;
    const observer = new MutationObserver(() => {
      restProps.onChange?.({
        target: {
          value: input.dataset.value
        }
      } as ChangeEvent<HTMLInputElement>);
    });

    observer.observe(input, { attributes: true, attributeFilter: ['value'] });

    return () => {
      observer.disconnect();
    };
  }, [restProps?.onChange]);

  /**
   * Эффект для начальной настройки состояния dateStore.
   * - При первом рендере устанавливает начальное состояние dateStore на основе `value` или `defaultValue`.
   */
  useEffect(() => {
    const initialDate = value || defaultValue;

    if (initialRender.current && initialDate) {
      dateStore.setInitialState(String(initialDate));
      initialRender.current = false;
    }

    if (!value && !defaultValue && currentLanguage.current !== language) {
      dateStore.resetDateStore();
      currentLanguage.current = language;
    }
  }, [restProps?.value, restProps?.defaultValue]);

  /**
   * Эффект для сброса состояния dateStore при размонтировании компонента.
   * - Гарантирует очистку состояния dateStore при удалении компонента.
   */
  useEffect(() => {
    currentLanguage.current = language;
    return () => {
      dateStore.resetDateStore();
    };
  }, []);

  return {
    inputRef
  };
};
